import React from "react";
import { QRCode } from "@progress/kendo-react-barcodes";
import {
	PaymentSlipDataInput,
	formatPaymentSlipData,
} from "../../../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { convertToUtf8 } from "../../../../pdf-templates/utils";

interface QrCodeRendererProps {
	paymentSlipData: PaymentSlipDataInput;
}

interface ErrorBoundaryProps {
	children: React.ReactNode;
	inputString?: string;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: React.ErrorInfo | null;
}

export class QrCodeErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error, errorInfo: null };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("QR Code Error:", error, errorInfo);
		this.setState({ errorInfo });
	}

	analyzeString(str: string) {
		const lines = str.split('\n');
		const analysis = {
			totalLength: str.length,
			lineCount: lines.length,
			problematicChars: [] as Array<{
				char: string;
				charCode: number;
				position: number;
				line: number;
				column: number;
				description: string;
			}>,
			encoding: this.detectEncoding(str),
			hasNullBytes: false,
			invisibleChars: [] as Array<{
				char: string;
				charCode: number;
				position: number;
				line: number;
				column: number;
				description: string;
			}>,
			whitespaceIssues: [] as Array<{
				line: number;
				issue: string;
				detail: string;
			}>,
		};

		let position = 0;
		lines.forEach((line, lineIndex) => {
			if (line.endsWith(' ')) {
				const trailingSpaces = line.length - line.trimEnd().length;
				analysis.whitespaceIssues.push({
					line: lineIndex + 1,
					issue: 'Trailing spaces',
					detail: `${trailingSpaces} Leerzeichen am Ende der Zeile`,
				});
			}

			if (line.endsWith('\t')) {
				analysis.whitespaceIssues.push({
					line: lineIndex + 1,
					issue: 'Trailing tab',
					detail: 'Tab-Zeichen am Ende der Zeile',
				});
			}

			if (line.includes('\t')) {
				const tabPositions = [];
				for (let i = 0; i < line.length; i++) {
					if (line[i] === '\t') tabPositions.push(i + 1);
				}
				analysis.whitespaceIssues.push({
					line: lineIndex + 1,
					issue: 'Tab character',
					detail: `Tab-Zeichen an Position(en): ${tabPositions.join(', ')}`,
				});
			}

			for (let i = 0; i < line.length; i++) {
				const char = line[i];
				const charCode = char.charCodeAt(0);

				if (charCode === 0) {
					analysis.hasNullBytes = true;
					analysis.invisibleChars.push({
						char: '\\0',
						charCode,
						position: position + i,
						line: lineIndex + 1,
						column: i + 1,
						description: 'NULL byte',
					});
				}

				if (charCode === 160) {
					analysis.invisibleChars.push({
						char: 'NBSP',
						charCode,
						position: position + i,
						line: lineIndex + 1,
						column: i + 1,
						description: 'Non-breaking space (0xA0)',
					});
				}

				if (charCode < 32 && charCode !== 10 && charCode !== 13 && charCode !== 9) {
					analysis.invisibleChars.push({
						char: `\\x${charCode.toString(16).toUpperCase().padStart(2, '0')}`,
						charCode,
						position: position + i,
						line: lineIndex + 1,
						column: i + 1,
						description: this.getControlCharName(charCode),
					});
				}

				if (charCode === 8203 || charCode === 8204 || charCode === 8205 || charCode === 65279) {
					analysis.invisibleChars.push({
						char: this.getInvisibleCharName(charCode),
						charCode,
						position: position + i,
						line: lineIndex + 1,
						column: i + 1,
						description: 'Unsichtbares Unicode-Zeichen',
					});
				}

				if (charCode > 127 && charCode < 160 && charCode !== 160) {
					analysis.problematicChars.push({
						char,
						charCode,
						position: position + i,
						line: lineIndex + 1,
						column: i + 1,
						description: 'C1 Control Character',
					});
				}

				if (charCode > 255) {
					analysis.problematicChars.push({
						char,
						charCode,
						position: position + i,
						line: lineIndex + 1,
						column: i + 1,
						description: 'Unicode (außerhalb ISO-8859-1)',
					});
				}
			}
			position += line.length + 1;
		});

		return analysis;
	}

	getControlCharName(charCode: number): string {
		const names: { [key: number]: string } = {
			0: 'NULL', 1: 'SOH', 2: 'STX', 3: 'ETX', 4: 'EOT', 5: 'ENQ',
			6: 'ACK', 7: 'BEL', 8: 'BS', 11: 'VT', 12: 'FF', 14: 'SO',
			15: 'SI', 16: 'DLE', 17: 'DC1', 18: 'DC2', 19: 'DC3', 20: 'DC4',
			21: 'NAK', 22: 'SYN', 23: 'ETB', 24: 'CAN', 25: 'EM', 26: 'SUB',
			27: 'ESC', 28: 'FS', 29: 'GS', 30: 'RS', 31: 'US',
		};
		return names[charCode] || `Control Char ${charCode}`;
	}

	getInvisibleCharName(charCode: number): string {
		const names: { [key: number]: string } = {
			8203: 'ZERO WIDTH SPACE',
			8204: 'ZERO WIDTH NON-JOINER',
			8205: 'ZERO WIDTH JOINER',
			65279: 'ZERO WIDTH NO-BREAK SPACE (BOM)',
		};
		return names[charCode] || `U+${charCode.toString(16).toUpperCase()}`;
	}

	detectEncoding(str: string) {
		const hasExtendedAscii = /[\x80-\xFF]/.test(str);
		const hasUnicode = /[^\x00-\xFF]/.test(str);

		if (hasUnicode) return "Unicode (außerhalb ISO-8859-1)";
		if (hasExtendedAscii) return "Extended ASCII / ISO-8859-1";
		return "ASCII";
	}

	render() {
		if (this.state.hasError) {
			const analysis = this.props.inputString
				? this.analyzeString(this.props.inputString)
				: null;

			return (
				<div className="border-2 border-red-500 rounded-lg p-6 bg-red-50">
					<h3 className="text-lg font-bold text-red-800 mb-2">
						QR-Code Rendering Fehler
					</h3>
					<p className="text-red-700 mb-4">
						Der QR-Code konnte nicht generiert werden.
					</p>

					{this.state.error && (
						<div className="space-y-4">
							<div className="bg-white border border-red-300 rounded p-3">
								<p className="font-semibold text-sm text-gray-700 mb-1">
									Fehlermeldung:
								</p>
								<p className="font-mono text-sm text-red-800 break-words">
									{this.state.error.message}
								</p>
							</div>

							{analysis && (
								<div className="bg-white border border-red-300 rounded p-3">
									<p className="font-semibold text-sm text-gray-700 mb-2">
										String Analyse:
									</p>
									<div className="space-y-2 text-sm">
										<div className="grid grid-cols-2 gap-2">
											<span className="text-gray-600">Gesamtlänge:</span>
											<span className="font-mono">{analysis.totalLength} Zeichen</span>

											<span className="text-gray-600">Zeilen:</span>
											<span className="font-mono">{analysis.lineCount}</span>

											<span className="text-gray-600">Encoding:</span>
											<span className="font-mono">{analysis.encoding}</span>
										</div>

										{analysis.whitespaceIssues.length > 0 && (
											<div className="mt-3 pt-3 border-t border-yellow-200">
												<p className="font-semibold text-yellow-700 mb-2">
													⚠️ Whitespace-Probleme gefunden:
												</p>
												<div className="max-h-40 overflow-auto space-y-2">
													{analysis.whitespaceIssues.map((issue, idx) => (
														<div
															key={idx}
															className="bg-yellow-50 p-2 rounded text-xs font-mono"
														>
															<div className="flex justify-between items-start gap-2">
																<div>
																	<span className="text-yellow-700">
																		Zeile {issue.line}:
																	</span>
																	<span className="ml-2 text-gray-700">
																		{issue.issue}
																	</span>
																</div>
																<div className="text-xs text-gray-500">
																	{issue.detail}
																</div>
															</div>
														</div>
													))}
												</div>
												<p className="text-xs text-yellow-700 mt-2 font-semibold">
													⚠️ Trailing spaces oder Tabs können QR-Code Probleme verursachen!
												</p>
												<p className="text-xs text-gray-600 mt-1">
													💡 Tipp: Entfernen Sie alle Leerzeichen/Tabs am Ende der Zeilen.
												</p>
											</div>
										)}

										{analysis.invisibleChars.length > 0 && (
											<div className="mt-3 pt-3 border-t border-orange-200">
												<p className="font-semibold text-orange-700 mb-2">
													👻 Unsichtbare/Control-Zeichen gefunden:
												</p>
												<div className="max-h-40 overflow-auto space-y-2">
													{analysis.invisibleChars.map((char, idx) => (
														<div
															key={idx}
															className="bg-orange-50 p-2 rounded text-xs font-mono"
														>
															<div className="flex justify-between items-start gap-2">
																<div>
																	<span className="text-orange-700">
																		Zeile {char.line}, Spalte {char.column}:
																	</span>
																	<span className="ml-2 bg-orange-200 px-1 rounded">
																		{char.char}
																	</span>
																</div>
																<div className="text-right">
																	<div className="text-gray-600">
																		0x{char.charCode.toString(16).toUpperCase().padStart(2, '0')}
																	</div>
																	<div className="text-xs text-gray-500">
																		{char.description}
																	</div>
																</div>
															</div>
														</div>
													))}
												</div>
												<p className="text-xs text-orange-700 mt-2 font-semibold">
													⚠️ Dies ist wahrscheinlich die Ursache des Fehlers!
												</p>
												<p className="text-xs text-gray-600 mt-1">
													💡 Tipp: Entfernen Sie alle unsichtbaren Zeichen und Control-Characters aus dem String.
												</p>
											</div>
										)}

										{analysis.problematicChars.length > 0 && (
											<div className="mt-3 pt-3 border-t border-red-200">
												<p className="font-semibold text-red-700 mb-2">
													⚠️ Problematische Zeichen gefunden:
												</p>
												<div className="max-h-40 overflow-auto space-y-2">
													{analysis.problematicChars.map((char, idx) => (
														<div
															key={idx}
															className="bg-red-50 p-2 rounded text-xs font-mono"
														>
															<div className="flex justify-between items-start gap-2">
																<div>
																	<span className="text-red-700">
																		Zeile {char.line}, Spalte {char.column}:
																	</span>
																	<span className="ml-2 bg-red-200 px-1 rounded">
																		'{char.char}'
																	</span>
																</div>
																<div className="text-right">
																	<div className="text-gray-600">
																		U+{char.charCode.toString(16).toUpperCase().padStart(4, '0')}
																	</div>
																	<div className="text-xs text-gray-500">
																		{char.description}
																	</div>
																</div>
															</div>
														</div>
													))}
												</div>
												<p className="text-xs text-gray-600 mt-2">
													💡 Tipp: Zeichen außerhalb von ASCII (0-127) können zu Problemen führen.
													Verwenden Sie nur lateinische Zeichen ohne Umlaute für den Test.
												</p>
											</div>
										)}

										{analysis.problematicChars.length === 0 && analysis.invisibleChars.length === 0 && analysis.whitespaceIssues.length === 0 && (
											<p className="text-xs text-gray-600 mt-2">
												Keine offensichtlichen Zeichenprobleme gefunden.
												Der Fehler könnte strukturell bedingt sein.
											</p>
										)}
									</div>
								</div>
							)}

							{this.state.error.stack && (
								<details className="bg-white border border-red-300 rounded p-3">
									<summary className="cursor-pointer text-sm text-red-600 hover:text-red-800 font-medium">
										Stack Trace anzeigen
									</summary>
									<pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-40">
										{this.state.error.stack}
									</pre>
								</details>
							)}

							{this.state.errorInfo && (
								<details className="bg-white border border-red-300 rounded p-3">
									<summary className="cursor-pointer text-sm text-red-600 hover:text-red-800 font-medium">
										Component Stack anzeigen
									</summary>
									<pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-40">
										{this.state.errorInfo.componentStack}
									</pre>
								</details>
							)}
						</div>
					)}

					<button
						className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
						onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
						aria-label="Erneut versuchen"
					>
						Erneut versuchen
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}

export const QrCodeRenderer: React.FC<QrCodeRendererProps> = ({
	paymentSlipData,
}) => {
	const {
		success: formatSuccess,
		data: formattedPaymentSlipData,
		qrReceipt,
		error: formatError,
	} = formatPaymentSlipData(paymentSlipData);

	if (!formattedPaymentSlipData || !qrReceipt) {
		return (
			<div className="border-2 border-amber-500 rounded-lg p-6 bg-amber-50">
				<h3 className="text-lg font-bold text-amber-800 mb-2">
					Validierungsfehler
				</h3>
				<p className="text-amber-700 mb-2">
					Die Zahlschein-Daten sind ungültig.
				</p>
				<div className="bg-white border border-amber-300 rounded p-3">
					<p className="font-medium text-gray-800 mb-1">Fehler:</p>
					<p className="text-red-600">{formatError}</p>
				</div>
				<p className="text-sm text-amber-600 mt-4">
					Bitte überprüfen Sie die eingegebenen Daten und stellen Sie
					sicher, dass alle erforderlichen Felder korrekt ausgefüllt
					sind.
				</p>
			</div>
		);
	}

	const qrReceiptUtf8Bytes = convertToUtf8(qrReceipt);

	return (
		<QrCodeErrorBoundary inputString={qrReceipt}>
			<div className="flex flex-col items-center gap-4">
				<div className="border-2 border-green-500 rounded-lg p-6 bg-white">
					<QRCode
						value={qrReceiptUtf8Bytes}
						errorCorrection="M"
						encoding="ISO_8859_1"
						overlay={{
							type: "swiss",
						}}
						style={{
							width: "200px",
							height: "200px",
						}}
					/>
				</div>
				<div className="bg-green-50 border border-green-200 rounded-lg p-4 w-full">
					<p className="text-green-800 font-medium text-center">
						QR-Code erfolgreich generiert
					</p>
				</div>
				<details className="w-full">
					<summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 font-medium">
						QR-Code Daten anzeigen
					</summary>
					<pre className="mt-2 text-xs text-gray-700 bg-gray-100 p-4 rounded overflow-auto max-h-96">
						{qrReceipt}
					</pre>
				</details>
			</div>
		</QrCodeErrorBoundary>
	);
};
